import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { api } from "../utils/api";
import { useAuth } from "./AuthContext";

const DataContext = createContext(null);

const AUTOMATION_DEFAULTS = {
  autoSmsEnabled: true,
  smsDaysBeforeDue: 3,
  smsFrequency: "daily",
  smsIncludeOverdue: true,
  smsIncludePending: true,
  smsPendingTemplate:
    "Hi {name}, this is a reminder that rent of {amount} for house {house} is due on {dueDate}. Please arrange payment on time. Thank you.",
  smsOverdueTemplate:
    "Hi {name}, your rent for house {house} is now OVERDUE. An amount of {balance} is outstanding. Kindly settle immediately. Thank you.",
  smsTimeOfDay: "08:00",
  includedSmsProperties: [],
  autoCallEnabled: false,
  callOverdueOnly: true,
  callDaysAfterDue: 1,
  callTimeOfDay: "10:00",
  includedCallProperties: [],
};

export function DataProvider({ children }) {
  const { user, loading: authLoading } = useAuth();

  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [events, setEvents] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [utilityCharges, setUtilityCharges] = useState([]);
  const [onboarded, setOnboarded] = useState(false);
  const [billing, setBilling] = useState({
    billingMonth: "",
    billingCycleStart: "",
    billingDueDate: "",
    billingPeakTenants: 0,
    waived: false,
    waivedAt: null,
    waivedBy: null,
    waivedReason: null,
  });
  const [automationSettings, setAutomationSettings] = useState(AUTOMATION_DEFAULTS);
  const [loading, setLoading] = useState(true);
  // Track which user's data we've loaded. If user changes, data is stale.
  const [loadedUserId, setLoadedUserId] = useState(null);

  // Fetch all data when user changes (login/logout).
  useEffect(() => {
    if (authLoading) return; // Wait for auth to settle first.
    if (!user) {
      setProperties([]);
      setTenants([]);
      setPayments([]);
      setEvents([]);
      setCommunications([]);
      setUtilityCharges([]);
      setOnboarded(false);
      setBilling({
        billingMonth: "",
        billingCycleStart: "",
        billingDueDate: "",
        billingPeakTenants: 0,
        waived: false,
        waivedAt: null,
        waivedBy: null,
        waivedReason: null,
      });
      setAutomationSettings(AUTOMATION_DEFAULTS);
      setLoadedUserId(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoadedUserId(null);
    setLoading(true);

    // Core data needed for dashboard — load first.
    Promise.all([
      api.get("/properties").catch(() => ({ properties: [] })),
      api.get("/tenants").catch(() => ({ tenants: [] })),
      api.get("/payments").catch(() => ({ payments: [] })),
      api.get("/utility-charges").catch(() => ({ utilityCharges: [] })),
      api.get("/billing").catch(() => ({})),
    ]).then(([propData, tenData, payData, ucData, billData]) => {
      if (cancelled) return;
      setProperties(propData.properties || []);
      setTenants(tenData.tenants || []);
      setPayments(payData.payments || []);
      setUtilityCharges(ucData.utilityCharges || []);
      setOnboarded(billData.onboarded || false);
      setBilling({
        billingMonth: billData.billingMonth || "",
        billingCycleStart: billData.billingCycleStart || "",
        billingDueDate: billData.billingDueDate || "",
        billingPeakTenants: billData.billingPeakTenants || 0,
        waived: !!billData.waived,
        waivedAt: billData.waivedAt || null,
        waivedBy: billData.waivedBy || null,
        waivedReason: billData.waivedReason || null,
      });
      setAutomationSettings({
        ...AUTOMATION_DEFAULTS,
        ...(billData.automationSettings || {}),
      });
      setLoadedUserId(user?.id || "done");
      setLoading(false);

      // Secondary data — lazy-load in background after dashboard is ready.
      Promise.all([
        api.get("/events?limit=500").catch(() => ({ events: [] })),
        api.get("/communications").catch(() => ({ communications: [] })),
      ]).then(([evtData, commData]) => {
        if (cancelled) return;
        setEvents(evtData.events || []);
        setCommunications(commData.communications || []);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  // Refresh events from server (called after mutations that log events).
  const refreshEvents = useCallback(async () => {
    try {
      const data = await api.get("/events?limit=500");
      setEvents(data.events || []);
    } catch {
      // silent
    }
  }, []);

  // Refresh billing data from server.
  const refreshBilling = useCallback(async () => {
    try {
      const data = await api.get("/billing");
      setBilling({
        billingMonth: data.billingMonth || "",
        billingCycleStart: data.billingCycleStart || "",
        billingDueDate: data.billingDueDate || "",
        billingPeakTenants: data.billingPeakTenants || 0,
        waived: !!data.waived,
        waivedAt: data.waivedAt || null,
        waivedBy: data.waivedBy || null,
        waivedReason: data.waivedReason || null,
      });
      setOnboarded(data.onboarded || false);
    } catch {
      // silent
    }
  }, []);

  // ── Properties ──────────────────────────────────────────────────────

  const addProperty = useCallback(async (draft) => {
    const data = await api.post("/properties", draft);
    const p = data.property;
    setProperties((prev) => [p, ...prev]);
    refreshEvents();
    return p;
  }, [refreshEvents]);

  const updateProperty = useCallback(async (id, patch) => {
    const data = await api.put(`/properties/${id}`, patch);
    setProperties((prev) =>
      prev.map((p) => (p.id === id ? data.property : p))
    );
  }, []);

  const deleteProperty = useCallback(async (id) => {
    await api.delete(`/properties/${id}`);
    setProperties((prev) => prev.filter((p) => p.id !== id));
    setTenants((prev) =>
      prev.map((t) => (t.propertyId === id ? { ...t, status: "VACATED" } : t))
    );
  }, []);

  // ── Tenants ─────────────────────────────────────────────────────────

  const findActiveAt = useCallback(
    (propertyId, houseNumber, ignoreId = null) => {
      const house = String(houseNumber || "").trim().toLowerCase();
      return tenants.find(
        (t) =>
          t.id !== ignoreId &&
          t.status === "ACTIVE" &&
          t.propertyId === propertyId &&
          String(t.houseNumber).trim().toLowerCase() === house
      );
    },
    [tenants]
  );

  const addTenant = useCallback(async (draft) => {
    const data = await api.post("/tenants", draft);
    const t = data.tenant;
    setTenants((prev) => [t, ...prev]);
    refreshEvents();
    refreshBilling();
    return t;
  }, [refreshEvents, refreshBilling]);

  const bulkCreateTenants = useCallback(async (tenantsList) => {
    const data = await api.post("/tenants/bulk", { tenants: tenantsList });
    if (data.tenants?.length) {
      setTenants((prev) => [...data.tenants, ...prev]);
    }
    refreshEvents();
    refreshBilling();
    return data;
  }, [refreshEvents, refreshBilling]);

  const updateTenant = useCallback(async (id, patch) => {
    const data = await api.put(`/tenants/${id}`, patch);
    setTenants((prev) =>
      prev.map((t) => (t.id === id ? data.tenant : t))
    );
  }, []);

  const deleteTenant = useCallback(async (id) => {
    await api.delete(`/tenants/${id}`);
    setTenants((prev) => prev.filter((t) => t.id !== id));
    refreshEvents();
    refreshBilling();
  }, [refreshEvents, refreshBilling]);

  const vacateTenant = useCallback(async (id) => {
    await api.post(`/tenants/${id}/vacate`);
    setTenants((prev) =>
      prev.map((t) => (t.id === id ? { ...t, status: "VACATED" } : t))
    );
    refreshEvents();
    refreshBilling();
  }, [refreshEvents, refreshBilling]);

  const addTenantNote = useCallback(async (tenantId, text) => {
    if (!text?.trim()) return;
    const data = await api.post("/events/note", { tenantId, text: text.trim() });
    const evt = data.event;
    setEvents((prev) => [evt, ...prev]);
  }, []);

  const logDepositChange = useCallback(() => {
    // Deposit changes are logged as events server-side via tenant update
  }, []);

  // ── Payments ────────────────────────────────────────────────────────

  const recordPayment = useCallback(async (args) => {
    const data = await api.post("/payments/record", args);
    const rows = data.payments || [];
    setPayments((prev) => [...rows, ...prev]);
    refreshEvents();
    return rows;
  }, [refreshEvents]);

  const recordUtilityPayment = useCallback(async (args) => {
    const data = await api.post("/payments/record", {
      ...args,
      startMonth: args.month,
    });
    const rows = data.payments || [];
    setPayments((prev) => [...rows, ...prev]);
  }, []);

  const voidMonthPayments = useCallback(async (tenantId, month, chargeType) => {
    await api.post("/payments/void", { tenantId, month, chargeType });
    setPayments((prev) =>
      prev.filter((p) => {
        if (p.tenantId !== tenantId || p.month !== month) return true;
        if (chargeType) return (p.chargeType || "rent") !== chargeType;
        return false;
      })
    );
    refreshEvents();
  }, [refreshEvents]);

  // ── Utility Charges ─────────────────────────────────────────────────

  const addUtilityCharge = useCallback(async ({ tenantId, month, type, amount }) => {
    const data = await api.post("/utility-charges", { tenantId, month, type, amount });
    const uc = data.utilityCharge;
    setUtilityCharges((prev) => {
      const idx = prev.findIndex(
        (c) => c.tenantId === tenantId && c.month === month && c.type === type
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = uc;
        return next;
      }
      return [uc, ...prev];
    });
  }, []);

  const deleteUtilityCharge = useCallback(async (id) => {
    await api.delete(`/utility-charges/${id}`);
    setUtilityCharges((prev) => prev.filter((uc) => uc.id !== id));
  }, []);

  // ── Communications ──────────────────────────────────────────────────

  const refreshCommunications = useCallback(async () => {
    try {
      const data = await api.get("/communications");
      setCommunications(data.communications || []);
    } catch {
      // silent
    }
  }, []);

  const addCommunication = useCallback(async (draft) => {
    if (draft.type === "sms") {
      const data = await api.post("/communications/send-sms", {
        tenantId: draft.tenantId,
        message: draft.message || "",
      });
      const c = data.communication;
      setCommunications((prev) => [c, ...prev]);
      return c;
    }
    if (draft.type === "ai_call") {
      const data = await api.post("/ai-calls/call", {
        tenantId: draft.tenantId,
        lang: draft.lang || "en",
      });
      const c = data.communication;
      setCommunications((prev) => [c, ...prev]);
      return c;
    }
    return null;
  }, []);

  const initiateBulkAiCalls = useCallback(async (tenantIds, lang = "en") => {
    const data = await api.post("/ai-calls/bulk-call", { tenantIds, lang });
    const rows = data.communications || [];
    setCommunications((prev) => {
      const existingIds = new Set(prev.map((c) => c.id));
      const newOnly = rows.filter((r) => !existingIds.has(r.id));
      return [...newOnly, ...prev];
    });
    return data;
  }, []);

  const refreshCallTranscript = useCallback(async (commId) => {
    const data = await api.post(`/ai-calls/refresh/${commId}`);
    const updated = data.communication;
    setCommunications((prev) =>
      prev.map((c) => (c.id === commId ? updated : c))
    );
    return updated;
  }, []);

  const sendBulkSms = useCallback(async (messages) => {
    // messages: [{tenantId, message}, ...]
    const data = await api.post("/communications/send-bulk-sms", { messages });
    const rows = data.communications || [];
    setCommunications((prev) => {
      const existingIds = new Set(prev.map((c) => c.id));
      const newOnly = rows.filter((r) => !existingIds.has(r.id));
      return [...newOnly, ...prev];
    });
    return data;
  }, []);

  const syncInboundCalls = useCallback(async () => {
    const data = await api.post("/ai-calls/sync-inbound");
    const rows = data.communications || [];
    if (rows.length > 0) {
      setCommunications((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const newOnly = rows.filter((r) => !existingIds.has(r.id));
        return [...newOnly, ...prev];
      });
    }
    return data;
  }, []);

  const deleteCommunication = useCallback(async (id) => {
    await api.delete(`/communications/${id}`);
    setCommunications((prev) => prev.filter((c) => c.id !== id));
  }, []);

  // ── Billing / Automation ────────────────────────────────────────────

  const updateAutomationSettings = useCallback((patch) => {
    setAutomationSettings((prev) => ({ ...prev, ...patch }));
    api.put("/billing/settings", patch).catch(() => {});
  }, []);

  const finishOnboarding = useCallback(async () => {
    await api.post("/billing/onboarded");
    setOnboarded(true);
  }, []);

  const resetAll = useCallback(async () => {
    await api.post("/auth/clear-data");
    setProperties([]);
    setTenants([]);
    setPayments([]);
    setEvents([]);
    setCommunications([]);
    setUtilityCharges([]);
    setBilling({ billingMonth: "", billingPeakTenants: 0 });
    setAutomationSettings({ ...AUTOMATION_DEFAULTS });
    setOnboarded(false);
  }, []);

  const value = useMemo(
    () => ({
      properties,
      tenants,
      payments,
      events,
      billing,
      automationSettings,
      updateAutomationSettings,
      onboarded,
      addProperty,
      updateProperty,
      deleteProperty,
      addTenant,
      bulkCreateTenants,
      updateTenant,
      deleteTenant,
      vacateTenant,
      logDepositChange,
      addTenantNote,
      addCommunication,
      sendBulkSms,
      initiateBulkAiCalls,
      refreshCallTranscript,
      syncInboundCalls,
      refreshCommunications,
      refreshEvents,
      deleteCommunication,
      communications,
      utilityCharges,
      addUtilityCharge,
      deleteUtilityCharge,
      recordUtilityPayment,
      recordPayment,
      voidMonthPayments,
      findActiveAt,
      finishOnboarding,
      resetAll,
      loading,
      loadedUserId,
    }),
    [
      properties, tenants, payments, events, billing,
      automationSettings, updateAutomationSettings, onboarded, loadedUserId,
      addProperty, updateProperty, deleteProperty,
      addTenant, bulkCreateTenants, updateTenant, deleteTenant, vacateTenant,
      logDepositChange, addTenantNote,
      addCommunication, sendBulkSms, initiateBulkAiCalls, refreshCallTranscript,
      syncInboundCalls, refreshCommunications, refreshEvents, deleteCommunication, communications,
      utilityCharges, addUtilityCharge, deleteUtilityCharge,
      recordUtilityPayment,
      recordPayment, voidMonthPayments, findActiveAt,
      finishOnboarding, resetAll, loading,
    ]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used within DataProvider");
  return ctx;
}
